// src/pages/billing/BillingProductEditPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useBillingProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  ErrorAlert,
} from '@react/components';

export const BillingProductEditPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useBillingProduct();
  const { t } = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    unit: '',
    unit_label: '',
    is_active: true,
  });

  useEffect(() => {
    if (id) store.GetBillingProduct(id);
    return () => { store.clearCurrent(); };
  }, [id]);

  useEffect(() => {
    if (store.currentItem) {
      const item = store.currentItem as any;
      setFormData({
        name: item.name ?? '',
        description: item.description ?? '',
        unit: item.unit ?? '',
        unit_label: item.unit_label ?? '',
        is_active: item.is_active ?? true,
      });
    }
  }, [store.currentItem]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setIsSubmitting(true);
    try {
      await store.UpdateBillingProduct(id, formData as any);
      navigate(`/billing/products/${id}`);
    } catch {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  if (store.isLoading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">{t('common.loading')}</div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/billing/products" className="text-muted-foreground hover:text-foreground">Products</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('common.edit')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">Edit Product</h1>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader><CardTitle>Product Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <Input label="Name" type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required />
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
              <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[80px]" value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Unit" type="text" value={formData.unit} onChange={(e) => handleChange('unit', e.target.value)} />
              <Input label="Unit Label" type="text" value={formData.unit_label} onChange={(e) => handleChange('unit_label', e.target.value)} />
            </div>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={formData.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
              <span className="text-sm font-medium text-foreground">Active</span>
            </label>
          </CardContent>
          <CardFooter className="flex justify-end gap-3">
            <Link to={`/billing/products/${id}`}><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
});

export default BillingProductEditPage;
